import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Button,
  Label,
  Input,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsItem,
} from '../ui';
import { useToast } from '../../lib/hooks/useToast';
import { useUserData } from '../../lib/auth';
import { ficConfigService, type FICVatType, type FICPaymentMethod } from '../../lib/services/ficConfigService';
import {
  Loader2,
  Plus,
  Trash2,
  FileText,
  User,
  CreditCard,
  Settings,
  Receipt,
  Building,
  MapPin,
  X
} from 'lucide-react';
import type { 
  FICInvoiceFormData, 
  FICEntity, 
  FICItem, 
  DocumentType,
  PaymentTermsType,
  EInvoicePaymentMethod,
  EInvoiceType
} from '../../lib/types/fattureincloud';

interface EnhancedInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  dealId: string;
  queueItem?: any;
  queueItemId?: string;
  initialData?: Partial<FICInvoiceFormData>;
  onSave: (data: FICInvoiceFormData) => Promise<void>;
  mode: 'create' | 'edit' | 'view';
}

export function EnhancedInvoiceModal({
  isOpen,
  onClose,
  dealId,
  queueItem,
  queueItemId,
  initialData,
  onSave,
  mode
}: EnhancedInvoiceModalProps) {
  const toast = useToast();
  const { organizationId } = useUserData();

  // Stato per l'applicazione della marca da bollo (che rende la fattura esente IVA)
  const [applyStampDuty, setApplyStampDuty] = useState(false);
  // Salva le aliquote IVA originali per poterle ripristinare
  const [originalVatRates, setOriginalVatRates] = useState<number[]>([]);
  // Flag per sapere se abbiamo già inizializzato lo stato del bollo
  const [stampDutyInitialized, setStampDutyInitialized] = useState(false);

  // Stato per il regime fiscale: 'standard' = IVA normale, 'rivalsa_inps' = Rivalsa INPS 4%
  type TaxRegime = 'standard' | 'rivalsa_inps';
  const [taxRegime, setTaxRegime] = useState<TaxRegime>('standard');

  // Stato per le aliquote IVA dinamiche da FIC
  const [ficVatTypes, setFicVatTypes] = useState<FICVatType[]>([]);
  const [loadingVatTypes, setLoadingVatTypes] = useState(false);

  // Stato per i metodi di pagamento da FIC
  const [ficPaymentMethods, setFicPaymentMethods] = useState<FICPaymentMethod[]>([]);

  // Form state
  const [formData, setFormData] = useState<FICInvoiceFormData>({
    type: 'invoice',
    entity: {
      name: '',
      type: 'company'
    },
    date: new Date().toISOString().split('T')[0],
    subject: '',
    currency: 'EUR',
    language: 'it',
    items: [{
      name: '',
      description: '',
      qty: 1,
      measure: 'nr',
      net_price: 0,
      vat: { value: 22, description: '22%' }
    }],
    payment_terms: {
      days: 30,
      type: 'standard'
    },
    use_split_payment: false,
    use_gross_prices: false,
    e_invoice: false,
    ei_type: 'B2B'
  });

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  // Check if read-only mode
  const isReadOnly = mode === 'view';

  // Load initial data and FIC config for stamp duty default
  useEffect(() => {
    if (isOpen) {
      // Reset stamp duty initialization flag when modal opens
      setStampDutyInitialized(false);

      // Priority: queueItem.fic_data > initialData > defaults
      if (queueItem?.fic_data) {
        // Sincronizza fic_data.date con queueItem.due_date se disponibile
        const syncedFicData = {
          ...queueItem.fic_data,
          date: queueItem.due_date || queueItem.fic_data.date
        };
        setFormData(syncedFicData);
      } else if (initialData) {
        setFormData(prev => ({
          ...prev,
          ...initialData
        }));
      }
    }
  }, [isOpen, queueItem, initialData]);

  // Carica le aliquote IVA e metodi di pagamento da FIC
  useEffect(() => {
    const loadFicData = async () => {
      if (!isOpen || !organizationId) return;

      setLoadingVatTypes(true);
      try {
        const [vatTypes, paymentMethods] = await Promise.all([
          ficConfigService.fetchVatRates(organizationId),
          ficConfigService.fetchPaymentMethods(organizationId)
        ]);
        setFicVatTypes(vatTypes);
        setFicPaymentMethods(paymentMethods);
      } catch (error) {
        console.error('Error loading FIC data:', error);
      } finally {
        setLoadingVatTypes(false);
      }
    };

    loadFicData();
  }, [isOpen, organizationId]);

  // Carica la config FIC per inizializzare applyStampDuty e rivalsa in base ai default
  useEffect(() => {
    const loadFicConfig = async () => {
      if (!isOpen || !organizationId || stampDutyInitialized) return;

      try {
        const config = await ficConfigService.getConfig(organizationId);
        console.log('FIC Config loaded:', {
          default_stamp_duty: config?.default_stamp_duty,
          default_rivalsa_enabled: config?.default_rivalsa_enabled,
          default_vat_rate_value: config?.default_vat_rate_value
        });

        // Applica l'aliquota IVA di default dalla config (se presente)
        if (config?.default_vat_rate_value !== null && config?.default_vat_rate_value !== undefined) {
          const defaultVatValue = config.default_vat_rate_value;
          const defaultVatDescription = config.default_vat_rate_name || `${defaultVatValue}%`;

          // Aggiorna gli items esistenti con l'aliquota di default
          setFormData(prev => ({
            ...prev,
            items: prev.items.map(item => ({
              ...item,
              vat: { value: defaultVatValue, description: defaultVatDescription }
            }))
          }));
        }

        // Applica il metodo di pagamento di default dalla config (se presente)
        if (config?.default_payment_method_id) {
          setFormData(prev => ({
            ...prev,
            payment_method: {
              id: config.default_payment_method_id!,
              name: config.default_payment_method_name || ''
            }
          }));
        }

        // Rivalsa INPS ha priorità sulla marca da bollo (sono incompatibili)
        if (config?.default_rivalsa_enabled) {
          console.log('default_rivalsa_enabled è TRUE, attivo rivalsa INPS');
          setTaxRegime('rivalsa_inps');
          setFormData(prev => ({ ...prev, rivalsa: 4 }));
        } else if (config?.default_stamp_duty) {
          // Se la config ha default_stamp_duty attivo, attiva il bollo automaticamente
          console.log('default_stamp_duty è TRUE, attivo il bollo');

          // Attiva sempre il toggle quando default_stamp_duty è true
          setApplyStampDuty(true);

          // Salva le aliquote IVA originali e setta a 0%
          setOriginalVatRates(formData.items.map(item => item.vat?.value || 22));
          setFormData(prev => ({
            ...prev,
            items: prev.items.map(item => ({
              ...item,
              vat: { value: 0, description: '0% - Esente' }
            })),
            stamp_duty: 2.00
          }));
        } else {
          // Anche se default_stamp_duty è false, controlla se i dati esistenti hanno stamp_duty
          const existingStampDuty = queueItem?.fic_data?.stamp_duty || initialData?.stamp_duty;
          if (existingStampDuty && existingStampDuty > 0) {
            setApplyStampDuty(true);
          }
          // Controlla anche rivalsa esistente
          const existingRivalsa = queueItem?.fic_data?.rivalsa || initialData?.rivalsa;
          if (existingRivalsa && existingRivalsa > 0) {
            setTaxRegime('rivalsa_inps');
          }
        }

        setStampDutyInitialized(true);
      } catch (error) {
        console.error('Error loading FIC config:', error);
        setStampDutyInitialized(true);
      }
    };

    loadFicConfig();
  }, [isOpen, organizationId, stampDutyInitialized]);

  // Document type options
  const documentTypes: { value: DocumentType; label: string }[] = [
    { value: 'invoice', label: 'Fattura' },
    { value: 'quote', label: 'Preventivo' },
    { value: 'proforma', label: 'Proforma' },
    { value: 'receipt', label: 'Ricevuta' },
    { value: 'delivery_note', label: 'DDT' },
    { value: 'credit_note', label: 'Nota di credito' }
  ];

  // Payment terms options
  const paymentTermsTypes: { value: PaymentTermsType; label: string }[] = [
    { value: 'standard', label: 'Standard' },
    { value: 'end_of_month', label: 'Fine mese' },
    { value: 'cash', label: 'Contanti' },
    { value: 'custom', label: 'Personalizzato' }
  ];

  // E-Invoice payment methods
  const eiPaymentMethods: { value: EInvoicePaymentMethod; label: string }[] = [
    { value: 'MP01', label: 'MP01 - Contanti' },
    { value: 'MP02', label: 'MP02 - Assegno' },
    { value: 'MP05', label: 'MP05 - Bonifico' },
    { value: 'MP08', label: 'MP08 - Carta di pagamento' },
    { value: 'MP09', label: 'MP09 - RID' },
    { value: 'MP12', label: 'MP12 - RIBA' },
    { value: 'MP19', label: 'MP19 - SEPA Direct Debit' }
  ];

  // Currency options
  const currencies = [
    { value: 'EUR', label: 'EUR - Euro' },
    { value: 'USD', label: 'USD - Dollar US' },
    { value: 'GBP', label: 'GBP - Sterlina' },
    { value: 'CHF', label: 'CHF - Franco svizzero' }
  ];

  // VAT rates - usa aliquote dinamiche da FIC, con fallback a quelle hardcoded
  const vatRates = ficVatTypes.length > 0
    ? ficVatTypes
        .filter(v => !v.is_disabled)
        .map(v => ({
          value: v.value,
          label: `${v.value}% - ${v.description}`
        }))
    : [
        { value: 0, label: '0% - Esente' },
        { value: 4, label: '4% - Ridotta' },
        { value: 10, label: '10% - Ridotta' },
        { value: 22, label: '22% - Ordinaria' }
      ];

  // Update form data
  const updateFormData = (updates: Partial<FICInvoiceFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  // Update entity data
  const updateEntity = (updates: Partial<FICEntity>) => {
    setFormData(prev => ({
      ...prev,
      entity: { ...prev.entity, ...updates }
    }));
  };

  // Add new item
  const addItem = () => {
    // Usa l'aliquota IVA del primo item esistente (che ha il default dalla config)
    // oppure fallback a 22%
    const defaultVat = formData.items[0]?.vat || { value: 22, description: '22%' };

    const newItem: FICItem = {
      name: '',
      description: '',
      qty: 1,
      measure: 'nr',
      net_price: 0,
      vat: defaultVat
    };

    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  };

  // Update item
  const updateItem = (index: number, updates: Partial<FICItem>) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, ...updates } : item
      )
    }));
  };

  // Remove item
  const removeItem = (index: number) => {
    if (formData.items.length > 1) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index)
      }));
    }
  };

  // Calculate totals (inclusa rivalsa INPS se attiva)
  const calculateTotals = () => {
    let netTotal = 0;
    let vatTotal = 0;
    let rivalsaAmount = 0;

    // Calcola il totale netto degli items
    formData.items.forEach(item => {
      const itemNet = item.qty * item.net_price;
      netTotal += itemNet;
    });

    // Calcola rivalsa se attiva (4% sull'imponibile)
    if (formData.rivalsa && formData.rivalsa > 0) {
      rivalsaAmount = netTotal * (formData.rivalsa / 100);
    }

    // Base imponibile IVA = netto + rivalsa
    const taxableBase = netTotal + rivalsaAmount;

    // Calcola IVA sulla base imponibile (proporzionalmente per item)
    formData.items.forEach(item => {
      const itemNet = item.qty * item.net_price;
      // Proporzione dell'item sul totale
      const proportion = netTotal > 0 ? itemNet / netTotal : 0;
      // IVA calcolata sulla quota proporzionale della base imponibile
      const itemTaxableBase = taxableBase * proportion;
      const itemVat = itemTaxableBase * (item.vat?.value || 0) / 100;
      vatTotal += itemVat;
    });

    return {
      net: netTotal,
      rivalsa: rivalsaAmount,
      taxableBase: taxableBase,
      vat: vatTotal,
      gross: taxableBase + vatTotal + (formData.stamp_duty || 0)
    };
  };

  // Verifica se la fattura è esente IVA (tutti gli items hanno vat.value = 0)
  const isVatExempt = formData.items.every(item => (item.vat?.value || 0) === 0);

  // Handler per applicare/rimuovere la marca da bollo
  // Quando attivo: setta tutti gli items a IVA 0% + stamp_duty = 2.00
  // Quando disattivo: ripristina le aliquote IVA originali
  const handleApplyStampDuty = (enabled: boolean) => {
    if (enabled) {
      // Disattiva rivalsa se attiva (incompatibili)
      if (taxRegime === 'rivalsa_inps') {
        setTaxRegime('standard');
        setFormData(prev => ({ ...prev, rivalsa: undefined }));
      }

      // Salva le aliquote IVA originali prima di modificarle
      setOriginalVatRates(formData.items.map(item => item.vat?.value || 22));

      // Setta tutti gli items a IVA 0% (esente)
      const updatedItems = formData.items.map(item => ({
        ...item,
        vat: { value: 0, description: '0% - Esente' }
      }));

      setFormData(prev => ({
        ...prev,
        items: updatedItems,
        stamp_duty: 2.00
      }));
    } else {
      // Ripristina le aliquote IVA originali
      const updatedItems = formData.items.map((item, index) => ({
        ...item,
        vat: {
          value: originalVatRates[index] || 22,
          description: `${originalVatRates[index] || 22}%`
        }
      }));

      setFormData(prev => ({
        ...prev,
        items: updatedItems,
        stamp_duty: 0
      }));
    }

    setApplyStampDuty(enabled);
  };

  // Handler per cambio regime fiscale (IVA standard vs Rivalsa INPS)
  const handleTaxRegimeChange = (regime: TaxRegime) => {
    setTaxRegime(regime);

    if (regime === 'rivalsa_inps') {
      // Imposta rivalsa 4%
      setFormData(prev => ({
        ...prev,
        rivalsa: 4
      }));

      // Se marca da bollo è attiva, disattivala (sono incompatibili)
      if (applyStampDuty) {
        handleApplyStampDuty(false);
      }
    } else {
      // Rimuovi rivalsa
      setFormData(prev => ({
        ...prev,
        rivalsa: undefined
      }));
    }
  };

  // Handle save
  const handleSave = async () => {
    try {
      setLoading(true);
      
      // Validation
      if (!formData.entity.name.trim()) {
        toast.error('Il nome del cliente è obbligatorio');
        return;
      }

      if (formData.items.some(item => !item.name.trim())) {
        toast.error('Tutti gli articoli devono avere un nome');
        return;
      }

      // Validazione per aziende italiane: richiedi almeno P.IVA o Codice Fiscale
      if (formData.entity.type === 'company' &&
          (formData.entity.country === 'Italy' || formData.entity.country === 'Italia' || !formData.entity.country)) {
        if (!formData.entity.vat_number?.trim() && !formData.entity.tax_code?.trim()) {
          toast.error('Per le aziende italiane è obbligatorio inserire almeno la Partita IVA o il Codice Fiscale');
          return;
        }
      }

      await onSave(formData);

      toast.success(mode === 'create' ? 'Fattura creata con successo' : 'Fattura aggiornata con successo');

      onClose();
    } catch (error) {
      console.error('Error saving invoice:', error);
      toast.error('Errore durante il salvataggio della fattura');
    } finally {
      setLoading(false);
    }
  };

  const totals = calculateTotals();

  return createPortal(
    <AnimatePresence mode="wait">
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 z-[9998]"
            onClick={onClose}
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="fixed top-0 bottom-0 right-0 w-full sm:w-[600px] lg:w-[700px] bg-white dark:bg-gray-900 shadow-xl z-[9999] flex flex-col border-l border-[rgba(55,53,47,0.09)] dark:border-gray-700"
          >
            {/* Header */}
            <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-[rgba(55,53,47,0.09)] dark:border-gray-700 bg-[#FBFBFA] dark:bg-gray-800/50">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-[#2383E2]" />
                <h2 className="text-lg font-semibold text-[#37352F] dark:text-white">
                  {mode === 'create' ? 'Nuova Fattura' : mode === 'view' ? 'Visualizza Fattura' : 'Modifica Fattura'}
                </h2>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-[#787774] dark:text-gray-400 hover:text-[#37352F] dark:hover:text-white hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-gray-700 rounded-md transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Tabs Navigation */}
            <div className="flex-shrink-0 px-6 py-3 border-b border-[rgba(55,53,47,0.09)] dark:border-gray-700 bg-white dark:bg-gray-900">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-5 bg-[#FBFBFA] dark:bg-gray-800 p-1 rounded-lg">
                  <TabsItem value="general" className="flex items-center gap-1.5 text-xs data-[state=active]:bg-white data-[state=active]:text-[#37352F] data-[state=active]:shadow-sm text-[#787774] rounded-md transition-all">
                    <FileText className="h-3.5 w-3.5" />
                    Generale
                  </TabsItem>
                  <TabsItem value="client" className="flex items-center gap-1.5 text-xs data-[state=active]:bg-white data-[state=active]:text-[#37352F] data-[state=active]:shadow-sm text-[#787774] rounded-md transition-all">
                    <User className="h-3.5 w-3.5" />
                    Cliente
                  </TabsItem>
                  <TabsItem value="items" className="flex items-center gap-1.5 text-xs data-[state=active]:bg-white data-[state=active]:text-[#37352F] data-[state=active]:shadow-sm text-[#787774] rounded-md transition-all">
                    <Receipt className="h-3.5 w-3.5" />
                    Articoli
                  </TabsItem>
                  <TabsItem value="payment" className="flex items-center gap-1.5 text-xs data-[state=active]:bg-white data-[state=active]:text-[#37352F] data-[state=active]:shadow-sm text-[#787774] rounded-md transition-all">
                    <CreditCard className="h-3.5 w-3.5" />
                    Pagamento
                  </TabsItem>
                  <TabsItem value="advanced" className="flex items-center gap-1.5 text-xs data-[state=active]:bg-white data-[state=active]:text-[#37352F] data-[state=active]:shadow-sm text-[#787774] rounded-md transition-all">
                    <Settings className="h-3.5 w-3.5" />
                    Avanzate
                  </TabsItem>
                </TabsList>
              </Tabs>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto">
              <Tabs value={activeTab} className={isReadOnly ? '[&_input]:pointer-events-none [&_textarea]:pointer-events-none [&_select]:pointer-events-none [&_input]:opacity-60 [&_textarea]:opacity-60' : ''}>

            {/* General Tab */}
            <TabsContent value="general" className="px-6 py-4">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="type">Tipo Documento</Label>
                    <Select
                      value={formData.type}
                      onValueChange={(value: DocumentType) => updateFormData({ type: value })}
                      disabled={isReadOnly}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {documentTypes.map(type => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="date">Data</Label>
                    <Input
                      id="date"
                      type="date"
                      value={formData.date}
                      onChange={(e) => updateFormData({ date: e.target.value })}
                      disabled={isReadOnly}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="currency">Valuta</Label>
                    <Select
                      value={formData.currency}
                      onValueChange={(value) => updateFormData({ currency: value })}
                      disabled={isReadOnly}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {currencies.map(currency => (
                          <SelectItem key={currency.value} value={currency.value}>
                            {currency.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="language">Lingua</Label>
                    <Select
                      value={formData.language}
                      onValueChange={(value) => updateFormData({ language: value })}
                      disabled={isReadOnly}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="it">Italiano</SelectItem>
                        <SelectItem value="en">English</SelectItem>
                        <SelectItem value="de">Deutsch</SelectItem>
                        <SelectItem value="fr">Français</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="subject">Oggetto</Label>
                  <Input
                    id="subject"
                    value={formData.subject || ''}
                    onChange={(e) => updateFormData({ subject: e.target.value })}
                    placeholder="Oggetto della fattura"
                    disabled={isReadOnly}
                  />
                </div>

                <div>
                  <Label htmlFor="notes">Note</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes || ''}
                    onChange={(e) => updateFormData({ notes: e.target.value })}
                    placeholder="Note aggiuntive"
                    rows={3}
                    disabled={isReadOnly}
                  />
                </div>
              </div>
            </TabsContent>

            {/* Client Tab */}
            <TabsContent value="client" className="px-6 py-4">
              {/* Sezione Dati Cliente */}
              <div className="pb-6 mb-6 border-b border-[rgba(55,53,47,0.09)] dark:border-gray-700">
                <div className="flex items-center gap-2 mb-4">
                  <Building className="h-4 w-4 text-[#787774]" />
                  <h3 className="text-sm font-medium text-[#37352F] dark:text-white">Dati Cliente</h3>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <input type="checkbox"
                      id="entity-type"
                      checked={formData.entity.type === 'company'}
                      onChange={(e) =>
                        updateEntity({ type: e.target.checked ? 'company' : 'person' })
                      }
                    />
                    <Label htmlFor="entity-type">
                      {formData.entity.type === 'company' ? 'Azienda' : 'Persona fisica'}
                    </Label>
                  </div>

                  {formData.entity.type === 'company' ? (
                    <div>
                      <Label htmlFor="business-name">Ragione Sociale *</Label>
                      <Input
                        id="business-name"
                        value={formData.entity.name}
                        onChange={(e) => updateEntity({ name: e.target.value })}
                        placeholder="Ragione sociale dell'azienda"
                      />
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="first-name">Nome *</Label>
                        <Input
                          id="first-name"
                          value={formData.entity.first_name || ''}
                          onChange={(e) => {
                            const firstName = e.target.value;
                            updateEntity({
                              first_name: firstName,
                              name: `${firstName} ${formData.entity.last_name || ''}`.trim()
                            });
                          }}
                          placeholder="Nome"
                        />
                      </div>
                      <div>
                        <Label htmlFor="last-name">Cognome *</Label>
                        <Input
                          id="last-name"
                          value={formData.entity.last_name || ''}
                          onChange={(e) => {
                            const lastName = e.target.value;
                            updateEntity({
                              last_name: lastName,
                              name: `${formData.entity.first_name || ''} ${lastName}`.trim()
                            });
                          }}
                          placeholder="Cognome"
                        />
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="vat-number">Partita IVA</Label>
                      <Input
                        id="vat-number"
                        value={formData.entity.vat_number || ''}
                        onChange={(e) => updateEntity({ vat_number: e.target.value })}
                        placeholder="IT00000000000"
                      />
                    </div>
                    <div>
                      <Label htmlFor="tax-code">Codice Fiscale</Label>
                      <Input
                        id="tax-code"
                        value={formData.entity.tax_code || ''}
                        onChange={(e) => updateEntity({ tax_code: e.target.value })}
                        placeholder="Codice fiscale"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.entity.email || ''}
                        onChange={(e) => updateEntity({ email: e.target.value })}
                        placeholder="email@esempio.com"
                      />
                    </div>
                    <div>
                      <Label htmlFor="certified-email">PEC</Label>
                      <Input
                        id="certified-email"
                        type="email"
                        value={formData.entity.certified_email || ''}
                        onChange={(e) => updateEntity({ certified_email: e.target.value })}
                        placeholder="pec@pec.it"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="phone">Telefono</Label>
                    <Input
                      id="phone"
                      value={formData.entity.phone || ''}
                      onChange={(e) => updateEntity({ phone: e.target.value })}
                      placeholder="+39 000 000 0000"
                    />
                  </div>
                </div>
              </div>

              {/* Sezione Indirizzo */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="h-4 w-4 text-[#787774]" />
                  <h3 className="text-sm font-medium text-[#37352F] dark:text-white">Indirizzo</h3>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="address">Via/Piazza</Label>
                    <Input
                      id="address"
                      value={formData.entity.address_street || ''}
                      onChange={(e) => updateEntity({ address_street: e.target.value })}
                      placeholder="Via Roma, 123"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="city">Città</Label>
                      <Input
                        id="city"
                        value={formData.entity.address_city || ''}
                        onChange={(e) => updateEntity({ address_city: e.target.value })}
                        placeholder="Milano"
                      />
                    </div>
                    <div>
                      <Label htmlFor="postal-code">CAP</Label>
                      <Input
                        id="postal-code"
                        value={formData.entity.address_postal_code || ''}
                        onChange={(e) => updateEntity({ address_postal_code: e.target.value })}
                        placeholder="20100"
                      />
                    </div>
                    <div>
                      <Label htmlFor="province">Provincia</Label>
                      <Input
                        id="province"
                        value={formData.entity.address_province || ''}
                        onChange={(e) => updateEntity({ address_province: e.target.value })}
                        placeholder="MI"
                        maxLength={2}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="country">Paese</Label>
                    <Input
                      id="country"
                      value={formData.entity.country || 'Italy'}
                      onChange={(e) => updateEntity({ country: e.target.value })}
                      placeholder="Italy"
                    />
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Items Tab */}
            <TabsContent value="items" className="px-6 py-4">
              {/* Header con titolo e bottone aggiungi */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Receipt className="h-4 w-4 text-[#787774]" />
                  <h3 className="text-sm font-medium text-[#37352F] dark:text-white">Articoli/Servizi</h3>
                </div>
                <Button onClick={addItem} size="sm" className="flex items-center gap-2 bg-[#2383E2] hover:bg-[#1a6fc0]">
                  <Plus className="h-4 w-4" />
                  Aggiungi
                </Button>
              </div>

              {/* Lista articoli */}
              <div className="space-y-4">
                {formData.items.map((item, index) => (
                  <div key={index} className="border-l-4 border-l-[#2383E2] pl-4 py-3 bg-[#FBFBFA] dark:bg-gray-800/50 rounded-r-md">
                    <div className="flex justify-between items-start mb-3">
                      <span className="text-xs font-medium text-[#787774] uppercase">Articolo {index + 1}</span>
                      {formData.items.length > 1 && (
                        <button
                          onClick={() => removeItem(index)}
                          className="text-red-500 hover:text-red-700 p-1"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <Label className="text-xs">Nome *</Label>
                        <Input
                          value={item.name}
                          onChange={(e) => updateItem(index, { name: e.target.value })}
                          placeholder="Nome articolo/servizio"
                          className="h-9"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Codice</Label>
                        <Input
                          value={item.code || ''}
                          onChange={(e) => updateItem(index, { code: e.target.value })}
                          placeholder="Opzionale"
                          className="h-9"
                        />
                      </div>
                    </div>

                    <div className="mb-3">
                      <Label className="text-xs">Descrizione</Label>
                      <Textarea
                        value={item.description || ''}
                        onChange={(e) => updateItem(index, { description: e.target.value })}
                        placeholder="Descrizione dettagliata"
                        rows={2}
                        className="text-sm"
                      />
                    </div>

                    <div className="grid grid-cols-4 gap-3">
                      <div>
                        <Label className="text-xs">Qtà</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.qty}
                          onChange={(e) => updateItem(index, { qty: parseFloat(e.target.value) || 0 })}
                          className="h-9"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Unità</Label>
                        <Input
                          value={item.measure || 'nr'}
                          onChange={(e) => updateItem(index, { measure: e.target.value })}
                          placeholder="nr"
                          className="h-9"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Prezzo</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.net_price}
                          onChange={(e) => updateItem(index, { net_price: parseFloat(e.target.value) || 0 })}
                          className="h-9"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">IVA</Label>
                        {applyStampDuty ? (
                          <div className="flex items-center h-9 px-2 bg-amber-50 border border-amber-200 rounded-md">
                            <span className="text-amber-700 text-xs font-medium">0% Esente</span>
                          </div>
                        ) : (
                          <Select
                            value={item.vat?.value?.toString() || '22'}
                            onValueChange={(value) => updateItem(index, {
                              vat: {
                                value: parseInt(value),
                                description: `${value}%`
                              }
                            })}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {vatRates.map(rate => (
                                <SelectItem key={rate.value} value={rate.value.toString()}>
                                  {rate.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    </div>

                    <div className="mt-3 pt-3 border-t border-[rgba(55,53,47,0.09)] dark:border-gray-700 flex justify-between text-xs text-[#787774]">
                      <span>Netto: €{(item.qty * item.net_price).toFixed(2)}</span>
                      <span>IVA: €{(item.qty * item.net_price * (item.vat?.value || 0) / 100).toFixed(2)}</span>
                      <span className="font-semibold text-[#37352F] dark:text-white">
                        Tot: €{(item.qty * item.net_price * (1 + (item.vat?.value || 0) / 100)).toFixed(2)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* Payment Tab */}
            <TabsContent value="payment" className="px-6 py-4">
              <div className="flex items-center gap-2 mb-4">
                <CreditCard className="h-4 w-4 text-[#787774]" />
                <h3 className="text-sm font-medium text-[#37352F] dark:text-white">Condizioni di Pagamento</h3>
              </div>

              <div className="space-y-4">
                {/* Metodo di Pagamento FIC */}
                {ficPaymentMethods.length > 0 && (
                  <div>
                    <Label>Metodo di Pagamento</Label>
                    <Select
                      value={formData.payment_method?.id?.toString() || ''}
                      onValueChange={(value) => {
                        const pm = ficPaymentMethods.find(p => p.id.toString() === value);
                        if (pm) {
                          updateFormData({
                            payment_method: {
                              id: pm.id,
                              name: pm.name
                            }
                          });
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleziona metodo di pagamento" />
                      </SelectTrigger>
                      <SelectContent>
                        {ficPaymentMethods.map(pm => (
                          <SelectItem key={pm.id} value={pm.id.toString()}>
                            {pm.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Scadenza (giorni)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.payment_terms?.days || 30}
                      onChange={(e) => updateFormData({
                        payment_terms: {
                          ...formData.payment_terms,
                          days: parseInt(e.target.value) || 30
                        }
                      })}
                    />
                  </div>
                  <div>
                    <Label>Tipo Scadenza</Label>
                    <Select
                      value={formData.payment_terms?.type || 'standard'}
                      onValueChange={(value: PaymentTermsType) => updateFormData({
                        payment_terms: {
                          ...formData.payment_terms,
                          type: value
                        }
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {paymentTermsTypes.map(type => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Sezione Dati Bancari */}
                <div className="pt-4 mt-4 border-t border-[rgba(55,53,47,0.09)] dark:border-gray-700">
                  <span className="text-xs font-medium text-[#787774] uppercase">Dati Bancari</span>
                </div>

                <div>
                  <Label>Nome Banca</Label>
                  <Input
                    value={formData.bank_name || ''}
                    onChange={(e) => updateFormData({ bank_name: e.target.value })}
                    placeholder="Nome della banca"
                  />
                </div>

                <div>
                  <Label>IBAN</Label>
                  <Input
                    value={formData.bank_iban || ''}
                    onChange={(e) => updateFormData({ bank_iban: e.target.value })}
                    placeholder="IT60 X054 2811 1010 0000 0123 456"
                  />
                </div>

                <div>
                  <Label>Beneficiario</Label>
                  <Input
                    value={formData.bank_beneficiary || ''}
                    onChange={(e) => updateFormData({ bank_beneficiary: e.target.value })}
                    placeholder="Nome del beneficiario"
                  />
                </div>
              </div>
            </TabsContent>

            {/* Advanced Tab */}
            <TabsContent value="advanced" className="px-6 py-4">
              <div className="flex items-center gap-2 mb-4">
                <Settings className="h-4 w-4 text-[#787774]" />
                <h3 className="text-sm font-medium text-[#37352F] dark:text-white">Impostazioni Avanzate</h3>
              </div>

              <div className="space-y-4">
                {/* Opzioni base */}
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <input type="checkbox"
                      id="split-payment"
                      checked={formData.use_split_payment || false}
                      onChange={(e) => updateFormData({ use_split_payment: e.target.checked })}
                    />
                    <Label htmlFor="split-payment">Split Payment</Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <input type="checkbox"
                      id="gross-prices"
                      checked={formData.use_gross_prices || false}
                      onChange={(e) => updateFormData({ use_gross_prices: e.target.checked })}
                    />
                    <Label htmlFor="gross-prices">Prezzi IVA inclusa</Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <input type="checkbox"
                      id="e-invoice"
                      checked={formData.e_invoice || false}
                      onChange={(e) => updateFormData({ e_invoice: e.target.checked })}
                    />
                    <Label htmlFor="e-invoice">Fatturazione Elettronica</Label>
                  </div>
                </div>

                {/* E-Fattura settings */}
                {formData.e_invoice && (
                  <div className="space-y-3 p-3 border border-blue-200 rounded-md bg-blue-50/50 dark:bg-blue-900/20 dark:border-blue-800">
                    <span className="text-xs font-medium text-blue-700 dark:text-blue-300 uppercase">Fatturazione Elettronica</span>

                    <div>
                      <Label className="text-xs">Tipo E-Fattura</Label>
                      <Select
                        value={formData.ei_type || 'B2B'}
                        onValueChange={(value: EInvoiceType) => updateFormData({ ei_type: value })}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="B2B">B2B - Business to Business</SelectItem>
                          <SelectItem value="B2C">B2C - Business to Consumer</SelectItem>
                          <SelectItem value="PA">PA - Pubblica Amministrazione</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-xs">Modalità Pagamento</Label>
                      <Select
                        value={formData.ei_data?.payment_method || 'MP05'}
                        onValueChange={(value: EInvoicePaymentMethod) => updateFormData({
                          ei_data: {
                            ...formData.ei_data,
                            payment_method: value
                          }
                        })}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {eiPaymentMethods.map(method => (
                            <SelectItem key={method.value} value={method.value}>
                              {method.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {/* Sezione Regime Fiscale */}
                <div className="space-y-3 p-3 border border-[rgba(55,53,47,0.09)] rounded-md bg-[#FBFBFA] dark:bg-gray-800/50 dark:border-gray-700">
                  <span className="text-xs font-medium text-[#787774] uppercase">Regime Fiscale</span>

                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="tax-standard"
                        name="tax-regime"
                        checked={taxRegime === 'standard'}
                        onChange={() => handleTaxRegimeChange('standard')}
                        disabled={isReadOnly || applyStampDuty}
                      />
                      <Label htmlFor="tax-standard" className={`text-sm ${applyStampDuty ? 'text-gray-400' : ''}`}>
                        IVA 22% (standard)
                      </Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="tax-rivalsa"
                        name="tax-regime"
                        checked={taxRegime === 'rivalsa_inps'}
                        onChange={() => handleTaxRegimeChange('rivalsa_inps')}
                        disabled={isReadOnly || applyStampDuty}
                      />
                      <Label htmlFor="tax-rivalsa" className={`text-sm ${applyStampDuty ? 'text-gray-400' : ''}`}>
                        Rivalsa INPS 4% (+ IVA 22%)
                      </Label>
                    </div>
                  </div>

                  {taxRegime === 'rivalsa_inps' && (
                    <div className="text-xs text-blue-700 bg-blue-50 p-2 rounded border border-blue-200">
                      <strong>Rivalsa INPS attiva:</strong> 4% sull'imponibile, IVA sul totale.
                    </div>
                  )}

                  {applyStampDuty && (
                    <div className="text-xs text-[#787774] italic">
                      Regime bloccato: marca da bollo richiede fattura esente IVA.
                    </div>
                  )}
                </div>

                {/* Cassa e Ritenuta */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">Cassa Previdenziale (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.cassa || ''}
                      onChange={(e) => updateFormData({ cassa: parseFloat(e.target.value) || undefined })}
                      className="h-9"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Ritenuta d'Acconto (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.withholding_tax || ''}
                      onChange={(e) => updateFormData({ withholding_tax: parseFloat(e.target.value) || undefined })}
                      className="h-9"
                    />
                  </div>
                </div>

                {/* Sezione Marca da Bollo */}
                <div className="space-y-3 p-3 border border-[rgba(55,53,47,0.09)] rounded-md bg-[#FBFBFA] dark:bg-gray-800/50 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-xs font-medium text-[#787774] uppercase">Marca da Bollo</span>
                      <p className="text-xs text-[#787774] mt-0.5">
                        Fattura esente IVA (0%) + bollo €2.00
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="apply-stamp-duty"
                        checked={applyStampDuty}
                        onChange={(e) => handleApplyStampDuty(e.target.checked)}
                        disabled={isReadOnly}
                      />
                      <Label htmlFor="apply-stamp-duty" className="text-sm">
                        Applica
                      </Label>
                    </div>
                  </div>

                  {applyStampDuty && (
                    <div className="text-xs text-amber-700 bg-amber-50 p-2 rounded border border-amber-200">
                      <strong>Bollo attivo:</strong> Articoli esenti IVA, €2.00 applicato.
                    </div>
                  )}

                  <div className="flex items-center gap-2 text-xs">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-medium ${
                      isVatExempt
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {isVatExempt ? 'Esente IVA' : 'Con IVA'}
                    </span>
                    {applyStampDuty && (
                      <span className="text-green-600 font-medium">
                        ✓ €2.00
                      </span>
                    )}
                  </div>

                  <div>
                    <Label className="text-xs">Importo Bollo (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.stamp_duty || ''}
                      onChange={(e) => updateFormData({ stamp_duty: parseFloat(e.target.value) || undefined })}
                      disabled={isReadOnly || applyStampDuty}
                      className={`h-9 ${applyStampDuty ? 'bg-gray-100' : ''}`}
                    />
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>

            {/* Sticky Totals Bar */}
            <div className="flex-shrink-0 border-t border-[rgba(55,53,47,0.09)] dark:border-gray-700 bg-[#F7F7F5] dark:bg-gray-800 px-6 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 text-sm flex-wrap">
                  <div>
                    <span className="text-[#787774] dark:text-gray-400">Imponibile:</span>
                    <span className="ml-1 font-semibold text-[#37352F] dark:text-white">€{totals.net.toFixed(2)}</span>
                  </div>
                  {totals.rivalsa > 0 && (
                    <div>
                      <span className="text-[#787774] dark:text-gray-400">Rivalsa INPS:</span>
                      <span className="ml-1 font-semibold text-blue-600">€{totals.rivalsa.toFixed(2)}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-[#787774] dark:text-gray-400">IVA:</span>
                    <span className="ml-1 font-semibold text-[#37352F] dark:text-white">€{totals.vat.toFixed(2)}</span>
                  </div>
                  {(formData.stamp_duty || 0) > 0 && (
                    <div>
                      <span className="text-[#787774] dark:text-gray-400">Bollo:</span>
                      <span className="ml-1 font-semibold text-amber-600">€{(formData.stamp_duty || 0).toFixed(2)}</span>
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <span className="text-sm text-[#787774] dark:text-gray-400 mr-2">Totale:</span>
                  <span className="text-xl font-bold text-[#2383E2]">€{totals.gross.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Footer with action buttons */}
            <div className="flex-shrink-0 flex justify-end items-center gap-3 px-6 py-4 border-t border-[rgba(55,53,47,0.09)] dark:border-gray-700 bg-white dark:bg-gray-800">
              <Button variant="outline" onClick={onClose}>
                {isReadOnly ? 'Chiudi' : 'Annulla'}
              </Button>
              {!isReadOnly && (
                <Button onClick={handleSave} disabled={loading} className="bg-[#2383E2] hover:bg-[#1a6fc0]">
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {mode === 'create' ? 'Crea Fattura' : 'Salva Modifiche'}
                </Button>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}